'use client';

import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { fetcher } from '@/libs/fetcher/fetcher';
import useSWR from 'swr';

// Import Context
import { useEnvContext } from '@/context/envContext';
import { useLangContext } from '@/context/langContext';

// Import components
import BannerBlur from '@/components/Banners/BannerBlur/BannerBlur';
import AssetBanner from '@/components/Banners/AssetBanner/AssetBanner';
import ErrorBoundaryCheck from '@/components/ErrorBoundaryClass/ErrorBoundaryClass';
import SmartFormSection from '@/components/SmartFormSection/SmartFormSection';
import AssetDescription from '@/components/AssetDescription/AssetDescription';
import TopPicksSection from '@/components/TopPicksSection/TopPicksSection';

//Import libs
import { AssetProps } from '@/libs/interfaceProps/assetProps';
import { ParamsProps } from '@/libs/interfaceProps/paramsProps';
import FulfillmentPopup from '@/libs/fulfillmentPopup/fulfillmentPopup';
import { sanitizeTitle } from '@/libs/sanitizeTitle/sanitizeTitle';
import useResFallbackImages from '@/libs/resFallbackImages/resFallbackImages';
import { sitenameImageIndexHandler } from '@/libs/sitenameImageIndexHandler/sitenameImageIndexHandler';
import { CustomOrgProps } from '@/libs/interfaceProps/customOrgProps';
import { useCustomOrgContext } from '@/context/customOrgContext';
import { useOrgContext } from '@/context/orgContext';
import { fetchHostUrl } from '@/libs/fetchHostUrl/fetchHostUrl';
import { getSmartFormRedirectUrl } from '@/libs/getSmartFormRedirectUrl/getSmartFormRedirectUrl';
import { getUserStatusFromData } from '@/libs/getUserStatus/getUserStatus';
import { getAssetTypeFromParams } from '@/libs/getUserAssetType/getUserAssetType';
import { getUserAccessCode } from '@/libs/getUserAccessCode/getUserAccessCode';
import { getAppCode } from '@/libs/getAppCodeGA4/getAppCodeGA4';
import { pushGTMDisplayTags } from '@/libs/pushGTMTags/pushGTMTags';
import AssetMetadata from '@/components/AssetMetaData/AssetMetaData';
import AssetClientLogo from '@/components/ClientLogo/AssetClientLogo';
interface AssetsComponentProps {
  userEmailData: {
    email: string | false;
    accessCodeGA4: string;
  };
  params: ParamsProps;
  resourceData: AssetProps;
  queryParams: string;
  validationParams?: {
    customerLinks: boolean;
    leadDropValidation: boolean;
    peDemandSub: boolean;
  };
  docData?: {
    docUrl: string;
  };
  globalEnvConfig: any;
  customOrgIds: CustomOrgProps;
}
const Asset = ({
  userEmailData,
  params,
  resourceData,
  validationParams,
  docData,
  globalEnvConfig,
  queryParams,
  customOrgIds
}: AssetsComponentProps) => {
  const t = useTranslations('Asset');
  const pathname = usePathname();
  const {
    setCdnEnv,
    setWebUrl,
    setUserUrl,
    setChApi
  } = useEnvContext();
  const {
    setLang
  } = useLangContext();
  const {
    setCustomOrg
  } = useCustomOrgContext();
  const [isLoading, setLoading] = useState<boolean>(true);
  const [downloadReady, setDownloadReady] = useState<boolean>(false);
  const [showLogo1, setShowLogo1] = useState<boolean>(false);
  const [titleLineBreakClass, setTitleLineBreakClass] = useState<boolean>(false);
  const openedNewTabRef = useRef(false);
  const resFallbackImages = useResFallbackImages();
  const sitenameImageIndex: Record<string, number> = {};
  const [customTemplate, setCustomTemplate] = useState<boolean>(false);
  const {
    setOrgData
  } = useOrgContext();

  // Set popup url if we have it.
  let downloadData = docData;

  // Enabled the auto popup for top picks items.
  useEffect(() => {
    // Check if popup has already opened. If it
    if (!openedNewTabRef?.current) {
      // Check if we have asset url and we have validated lead drop
      // or
      // Check if smartform has validated and we have asset url.
      if (downloadData?.docUrl && validationParams?.leadDropValidation === true) {
        FulfillmentPopup(downloadData?.docUrl);
        openedNewTabRef.current = true;
      }
    }
  }, [downloadData?.docUrl, validationParams?.leadDropValidation, resourceData?.orgImages?.bannerMobile]);
  const userStatus = getUserStatusFromData(userEmailData);

  // if peDemandSub is true return 'sponsored' else 'unsponsored'
  const userAssetType = getAssetTypeFromParams(validationParams);

  // how user accessed - fastpass | passwordless | registration non member
  const userAccess = getUserAccessCode(userEmailData);

  // requirements: 
  // no reg form & user NOT logged in = 98 | No reg form but user IS logged in. = 99
  // {number to be decided } user loggedout and showing form.
  // leadDropValidationPassed && peDemandSub both true then appcode would be 99
  const appCode = getAppCode(userEmailData.email, validationParams, 'asset');

  // This useEfect runs twice; prevent this using useRef
  // This may not be required in production environments  if this is caused by
  // React 'StrictMode' which doesn't effect production, remove this before eng deployment to test.
  const hasRun = React.useRef(false);
  useEffect(() => {
    if (!hasRun.current) {
      pushGTMDisplayTags({
        appCode: appCode,
        contentID: params.resId,
        contentType: resourceData.docType,
        pageType: 'asset',
        memberStatus: userStatus,
        accessStatus: userAccess,
        // how user accessed - fastpass | passwordless | registration non member
        assetType: userAssetType,
        orgID: resourceData?.orgId,
        orgName: resourceData?.orgName
      });
      hasRun.current = true;
    }
  });
  useEffect(() => {
    setOrgData({
      images: resourceData.orgImages,
      orgName: '',
      orgDescription: '',
      orgUrl: '',
      orgNumFound: 0,
      hasCustomBanner: resourceData.hasOrgCustomBanner
    });
    setCustomTemplate(customOrgIds?.customTemplate);
    setCdnEnv(globalEnvConfig?.NEXT_PUBLIC_CDN_ENV);
    setWebUrl(globalEnvConfig?.NEXT_PUBLIC_WEB_URL);
    setUserUrl(globalEnvConfig?.NEXT_PUBLIC_USER_TT_URL);
    setChApi(globalEnvConfig?.NEXT_PUBLIC_CH_API);
    setLang(params.lang);
    setCustomOrg(customOrgIds);
    const smartformComponent = document.querySelector('smartforms-techtarget');
    if (smartformComponent) {
      smartformComponent.addEventListener('smartformsMessage', handleEvent);
    }

    //Add cleanup function to remove event listener
    return () => {
      if (smartformComponent) {
        smartformComponent.removeEventListener('smartformsMessage', handleEvent);
      }
    };
  }, [globalEnvConfig?.NEXT_PUBLIC_CDN_ENV, globalEnvConfig?.NEXT_PUBLIC_CH_API, globalEnvConfig?.NEXT_PUBLIC_USER_TT_URL, globalEnvConfig?.NEXT_PUBLIC_WEB_URL, params.lang, setLang, setCdnEnv, setWebUrl, setUserUrl, setChApi, customOrgIds, setCustomOrg, setOrgData, resourceData?.orgImages, resourceData.hasOrgCustomBanner]);
  const handleEvent = (event: any) => {
    // Look for an event to hide the skeleton loading for the smart form.
    if (event?.detail?.type === 'smartforms-loaded') {
      setLoading(false);
    }
    // Look for an event to show the download button.
    if (event?.detail?.type === 'smartforms-download-ready') {
      setDownloadReady(true);
    }
  };
  const resId = params.resId;
  const responseData = resourceData;
  const resFallbackImage = sitenameImageIndexHandler(sitenameImageIndex, resFallbackImages, responseData.primarySiteName, resId);
  let publishDate = new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium'
  }).format(new Date(resourceData.publishedDate));
  const currentHostUrl = fetchHostUrl(globalEnvConfig.NEXT_PUBLIC_WEB_URL);
  const resDesc = responseData?.abstract || (responseData?.shortDescription ? sanitizeTitle(responseData?.shortDescription) : '');
  const smartFormRedirectUrl = getSmartFormRedirectUrl(currentHostUrl, validationParams, resId, params, pathname, queryParams);

  //  Re-fetch document data if it isn't available.
  //  Faced an issue that when fastpass user lands on the asset page first time the data wasn't available.
  //  By refetching the doc would resolve that scenario.
  const apiUrl = `/hub/services/fulfillmentAsset?resId=${params.resId}&lang=${params.lang}`;
  const {
    data
  } = useSWR(downloadReady && !downloadData?.docUrl ? apiUrl : null, fetcher);
  // Updated the downloadData.
  // Check if we have data and the popup hasn't already been opened
  if (data && !openedNewTabRef?.current) {
    downloadData = data;
    FulfillmentPopup(data?.docUrl);
    openedNewTabRef.current = true;
  }
  return <ErrorBoundaryCheck data-sentry-element="ErrorBoundaryCheck" data-sentry-component="Asset" data-sentry-source-file="asset.tsx">
   <BannerBlur resId={params.resId} primarySiteName={resourceData.primarySiteName} fallBackImage={resFallbackImage} data-sentry-element="BannerBlur" data-sentry-source-file="asset.tsx" />

   <div className={'main-download-page'}>
    <AssetBanner resourceData={resourceData} customTemplate={customTemplate} customOrgIds={customOrgIds} data-sentry-element="AssetBanner" data-sentry-source-file="asset.tsx" />

    <div className={'main-content'}>
     <div className={'assets-panel-detail col-med-12 col-lg-7'}>
      <div className={'center-panel'}>
       <AssetMetadata docType={responseData.docType} publishDate={publishDate} title={responseData.bestTitle} data-sentry-element="AssetMetadata" data-sentry-source-file="asset.tsx" />

       <AssetClientLogo customTemplate={customTemplate} resourceData={resourceData} responseData={responseData} showLogo1={showLogo1} setShowLogo1={setShowLogo1} titleLineBreakClass={titleLineBreakClass} setTitleLineBreakClass={setTitleLineBreakClass} params={params} pathname={pathname} queryParams={queryParams} t={t} data-sentry-element="AssetClientLogo" data-sentry-source-file="asset.tsx" />

       <a href={`#resId-${resId}`} className={classnames('tt-button-lrg mobile-download-btn')}>
        {t('Download')}
       </a>

       <AssetDescription resDesc={resDesc} validationParams={validationParams} data-sentry-element="AssetDescription" data-sentry-source-file="asset.tsx" />
      </div>
     </div>

     <SmartFormSection resId={resId} responseData={responseData} params={{
          ...params,
          t
        }} smartFormRedirectUrl={smartFormRedirectUrl} globalEnvConfig={globalEnvConfig} validationParams={validationParams} isLoading={isLoading} downloadReady={downloadReady} downloadData={downloadData} resFallbackImage={resFallbackImage} data-sentry-element="SmartFormSection" data-sentry-source-file="asset.tsx" />
    </div>

    {/* If peDemandSub, leadDropValidation are true display the TopPicks section */}
    {downloadReady && <TopPicksSection t={t} responseData={responseData} resId={resId} validationParams={validationParams} queryParams={queryParams} params={params} />}
   </div>
  </ErrorBoundaryCheck>;
};
export default Asset;